import {
	IconButton,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
	useHasPermission,
} from "@dgs/core";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFilterQuery } from "../../../../shared/api/filterQueries";
import { CreateEventDrawer } from "../../../../shared/events/save/CreateEventDrawer";
import { useFilter } from "../../../../shared/filters";
import { EventFilterDrawer } from "../../../../shared/filters/events/EventFilterDrawer";
import { FilterBar } from "../../../../shared/filters/FilterBar";
import { EventIndexResource } from "../../../../shared/types/event";
import { PermissionName } from "../../../../shared/types/permission";
import { eventService } from "../../shared/api/event/eventService";
import { EventExportAction } from "../EventExportAction";
import { useDeleteEvent } from "../useDeleteEvent";
import { useEventSave } from "../useEventSave";
import { useEventListColumns } from "./useEventListColumns";

export const EventList: FC = () => {
	const { t } = useTranslation();
	const { activeFilterId, resetFilter, applyFilter, options: filterOptions } = useFilter();
	const filterDrawerProps = useEditDrawer();
	const navigate = useNavigate();
	const handleSave = useEventSave();
	const deleteEvent = useDeleteEvent();
	const { eventListColumns } = useEventListColumns({
		onDelete: deleteEvent,
		hasParticipantStatusColumn: filterOptions.some(({ field }) => field === "EVENT_PARTICIPATION_STATUS"),
	});
	const { data: predefinedFilter } = useFilterQuery({ filterId: activeFilterId });
	const canCreate = useHasPermission<PermissionName>(["event.create"]);
	const openFilterDrawer = useCallback(() => {
		filterDrawerProps.handleOpen(activeFilterId || "new");
	}, [activeFilterId, filterDrawerProps]);

	return (
		<>
			<PaginatedAutoDataList
				name="events"
				columns={eventListColumns}
				empty={t("No events available")}
				heading={t("Events")}
				onRowClick={(value) => navigate(`${value.id}/details`)}
				listFetchConfig={laravelPaginatedListFetchConfig(
					[...eventService.listKey, `${activeFilterId}`],
					(config: PaginatedListFetcherConfig<EventIndexResource>) =>
						eventService.filteredIndex(config, activeFilterId),
				)}
				initialSearch={{
					search: "",
					searchFields: [
						{ label: t("Name"), name: "event_translations.name" },
						{ label: t("Project number"), name: "projectNumber" },
						{ label: t("Project managers"), name: "projectManagers" },
						{ label: t("Subdomain"), name: "applications.subdomain" },
					],
				}}
				headerActions={
					<>
						{canCreate && (
							<CreateEventDrawer createPath="/events/create" detailsPath="/events/active" handleSubmit={handleSave} />
						)}
						<IconButton title={t("Open event filter")} onClick={openFilterDrawer} size="small" icon="filter" />
						<EventExportAction filterId={activeFilterId} onExport={eventService.exportEvents} />
					</>
				}
				headerChildren={<FilterBar filterId={activeFilterId} onRemove={resetFilter} onClick={openFilterDrawer} />}
			/>
			{filterDrawerProps.editEntityId && (
				<EventFilterDrawer {...filterDrawerProps} filter={predefinedFilter || null} onSubmit={applyFilter} />
			)}
		</>
	);
};
